/**
 * The Solution picker list — every Solution the actor can see, with its Account.
 *
 * Two screens pick a Solution from a list (a new Resource Plan, and linking a Staffing
 * Request), and both used to hold their own copy of this query with a hard-coded key.
 * One hook, one key from the factory, one ordering: alphabetical, and complete.
 */

import { useQuery } from "@tanstack/react-query";
import { jsonFetch } from "@/lib/json-fetch";
import { type SelectOption, sortOptions } from "@/lib/option-search";
import { solutionDashboardKeys } from "@/lib/query";

export interface SolutionPickerOption {
  id: string;
  name: string;
  account: { id: string; name: string };
  /** Effective Engagement Standard Tier — the Solution's override, else the Account's. */
  engagementStandardTierId: string | null;
}

interface DashboardSolutionsResponse {
  solutions: SolutionPickerOption[];
}

/** The picker list's one read — shared with the NOS Deal refresh, which reloads it. */
export function fetchDashboardSolutions(): Promise<DashboardSolutionsResponse> {
  return jsonFetch<DashboardSolutionsResponse>("/api/dashboard/solutions");
}

/** The response as `SearchableSelect` options: grouped by Account, alphabetical. */
export function toSolutionPickerOptions(data: DashboardSolutionsResponse): SelectOption[] {
  return sortOptions(
    data.solutions.map((s) => ({ value: s.id, label: s.name, group: s.account.name })),
  );
}

/**
 * The picker's options, ready to hand to `SearchableSelect`: the Account is the group, so
 * the list reads Account by Account with each Account's Solutions alphabetical underneath.
 * Sorted here as well as in the API's `ORDER BY` — the component must not depend on the
 * server's ordering to be right, and a caller mapping the raw list cannot forget to sort.
 */
export function useSolutionPickerOptions(enabled = true) {
  const query = useQuery({
    queryKey: solutionDashboardKeys.all,
    queryFn: fetchDashboardSolutions,
    enabled,
    select: toSolutionPickerOptions,
  });
  return query;
}

/**
 * Solution id → its effective Engagement Standard Tier id.
 *
 * A second `select` over the SAME query key, so it costs no extra request and cannot drift
 * from the option list beside it. The New Resource Plan form needs it to pre-select the Pod
 * Shape the tier defaults to; the picker itself has no use for a tier, which is why this is
 * separate rather than stuffed into `SelectOption`.
 */
export function useSolutionTierIds(enabled = true) {
  return useQuery({
    queryKey: solutionDashboardKeys.all,
    queryFn: fetchDashboardSolutions,
    enabled,
    select: (data) =>
      new Map(data.solutions.map((s) => [s.id, s.engagementStandardTierId] as const)),
  });
}
